import { apiPath } from '@/redux/apiPath';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { openLicenseModal, openPublicationsModal } from '@/redux/modal/modal.slice';
import { mainMapModelDescriptionSelector } from '@/redux/models/models.selectors';
import {
  diseaseLinkSelector,
  diseaseNameSelector,
  organismLinkSelector,
  organismNameSelector,
  projectNameSelector,
  projectSelector,
  versionSelector,
} from '@/redux/project/project.selectors';
import { DrawerHeading } from '@/shared/DrawerHeading';
import { LinkButton } from '@/shared/LinkButton';
import { useEffect, useState } from 'react';
import './ProjectInfoDrawer.styles.css';
import { BASE_API_URL, BASE_NEW_API_URL } from '@/constants';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { showToast } from '@/utils/showToast';
import { getError } from '@/utils/error-report/getError';
import { downloadFileFromUrl } from '@/redux/export/export.utils';
import { Button } from '@/shared/Button';
import Image from 'next/image';
import spinnerIcon from '@/assets/vectors/icons/spinner.svg';

export const ProjectInfoDrawer = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const diseaseName = useAppSelector(diseaseNameSelector);
  const diseaseLink = useAppSelector(diseaseLinkSelector);
  const organismLink = useAppSelector(organismLinkSelector);
  const organismName = useAppSelector(organismNameSelector);
  const projectName = useAppSelector(projectNameSelector);
  const project = useAppSelector(projectSelector).data;
  const version = useAppSelector(versionSelector);
  const description = useAppSelector(mainMapModelDescriptionSelector);

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const sourceDownloadLink = BASE_API_URL + apiPath.getSourceFile();
  const downloadZipLink = (BASE_NEW_API_URL + apiPath.getGeneratedZipFileForProject()).replace(
    '//',
    '/',
  );

  const showWarnings = async (): Promise<void> => {
    try {
      const response = await axiosInstanceNewAPI.get<string[]>(
        apiPath.getGeneratedZipFileLogsForProject(),
        {
          withCredentials: true,
        },
      );
      const countErrors = response.data.length;
      const message = `Export of layers was incomplete, there were ${countErrors} errors. See the CellDesigner file for details. This is caused by the limitations of the CellDesigner format. <ul class="max-w-md space-y-1 text-red-500 list-disc list-inside">${response.data
        .map(warning => `<li>${warning}</li>`)
        .join(' ')}</ul>`;
      showToast({ type: 'info', message, duration: 120000 });
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(getError({ error }));
    }
  };

  const downloadZipFile = () => {
    return () => {
      setIsDownloading(true);
      downloadFileFromUrl(downloadZipLink).finally(function () {
        setIsDownloading(false);
      });
      showWarnings();
    };
  };

  let licenseName: string = '';
  if (project) {
    licenseName = project.license ? project.license.name : project.customLicenseName;
  }
  const licenseExists = licenseName !== '';

  useEffect(() => {
    // dispatch(getPublications());
  }, [dispatch]);

  const onPublicationsClick = (): void => {
    dispatch(openPublicationsModal());
  };

  const onLicenseClick = (): void => {
    dispatch(openLicenseModal(licenseName));
  };

  return (
    <div data-testid="export-drawer" className="h-full max-h-full">
      <DrawerHeading title="Project info" />
      <div className="h-[calc(100%-93px)] max-h-[calc(100%-93px)] overflow-y-auto px-6">
        <p className="mt-6">
          Name: <span className="font-semibold">{projectName}</span>
        </p>
        <p className="mt-4">
          Version: <span className="font-semibold">{version}</span>
        </p>
        <div className="mt-4">Data:</div>
        <ul className="list-disc pl-6 ">
          <li className="mt-2 text-hyperlink-blue">
            <button type="button" onClick={onPublicationsClick} className="text-base font-semibold">
              Publications
            </button>
          </li>
          <li className="mt-2 text-hyperlink-blue">
            <a
              href="https://minerva.pages.uni.lu/doc/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
            >
              Manual
            </a>
          </li>
          {diseaseName && (
            <li className="mt-2 text-hyperlink-blue">
              <span className="text-black">Disease: </span>
              <a
                href={diseaseLink}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:underline"
              >
                {diseaseName}
              </a>
            </li>
          )}
          {licenseExists && (
            <li className="mt-2 text-hyperlink-blue">
              <span className="text-black">License: </span>
              <button
                type="button"
                onClick={onLicenseClick}
                className="w-full truncate text-base font-semibold"
                title={licenseName}
              >
                {licenseName}
              </button>
            </li>
          )}
          {organismName && (
            <li className="mt-2 text-hyperlink-blue">
              <span className="text-black">Organism: </span>
              <a
                href={organismLink}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:underline"
              >
                {organismName}
              </a>
            </li>
          )}
        </ul>
        <LinkButton className="mt-6" href={sourceDownloadLink} download="sourceFile.txt">
          Download source file
        </LinkButton>
        <Button
          data-testid="download-submap-button"
          variantStyles="primary"
          className="mt-6"
          onClick={downloadZipFile()}
          disabled={isDownloading}
        >
          {isDownloading && (
            <Image
              src={spinnerIcon}
              alt="spinner icon"
              height={12}
              width={12}
              className="mr-5 animate-spin"
            />
          )}
          Download current project files
        </Button>
        {description && (
          <div
            data-testid="project-description"
            className="anchor-tag mt-7 rounded-lg bg-cultured px-4 py-2"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
    </div>
  );
};
